import { SxProps } from "@mui/material";
import { StatusChip, StatusChipColor, StatusChipVariant } from "components/StatusChip";
import { WasteClassification, WasteKind } from "generated-graphql/graphql";

import { prettyPrintWasteStreamLabel } from "./Facilities/Facility/prettyPrintWasteClassification";

// Semantic, low-contrast chips per the design system. Severity rises
// neutral → info → warning → error so the classification reads at a glance.
const CLASSIFICATION_COLOR: Record<WasteClassification, StatusChipColor> = {
  [WasteClassification.Nonhazardous]: "default",
  [WasteClassification.StateRegulated]: "info",
  [WasteClassification.RcraHazardous]: "warning",
  [WasteClassification.RcraAcutelyHazardous]: "error",
};

interface WasteClassificationChipProps {
  classification: WasteClassification | null | undefined;
  /**
   * The management program, folded into the same chip. Severity color still
   * comes from the classification alone: the program is not a hazard level.
   */
  wasteKind?: WasteKind | null;
  /**
   * Both variants are specced; which one is right depends on the frame. The
   * container surfaces draw the filled chip, everywhere else still outlines —
   * hence the outlined default rather than StatusChip's own.
   */
  variant?: StatusChipVariant;
  sx?: SxProps;
}

/**
 * The waste stream's classification and management program as one
 * at-a-glance semantic chip.
 */
export function WasteClassificationChip({
  classification,
  wasteKind,
  variant = "outlined",
  sx,
}: WasteClassificationChipProps) {
  const label = prettyPrintWasteStreamLabel(classification, wasteKind);
  if (!label) return null;
  return (
    <StatusChip
      label={label}
      // A combined label runs long enough to clip in a narrow grid cell, and a
      // clipped chip drops either the program or the hazard class. The native
      // tooltip makes the whole label reachable without widening the column.
      title={label}
      color={classification ? CLASSIFICATION_COLOR[classification] : "default"}
      variant={variant}
      sx={sx}
    />
  );
}
